import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiGithub, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import './Projects.css';

const INITIAL_COUNT = 3;

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [showAll, setShowAll] = useState(false);

  const projects = [
    {
      id: 1,
      title: 'SK Enterprises',
      description: 'Developed a client-side digital solution focused on improving usability, online presence, and overall user experience through a clean and responsive web interface. Addressed business challenges through problem-solving and design thinking.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      technologies: ['React', 'CSS3', 'Framer Motion'],
      websiteUrl: 'https://www.skenterprisesnashik.in/',
      status: 'client-project',
    },
    {
      id: 2,
      title: 'HeritEdge',
      description: 'Developed a full-stack heritage portal combining React, Express, and AI to help devotees navigate the Nashik Kumbh Mela. It features an automated trip planner that generates custom schedules based on budget and pace, integrated with Leaflet maps for real-time distance and travel estimations.',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1782201428/Paritrana_logo_zxilzx.png',
      technologies: ['React', 'Node.js', 'MongoDB', 'Tailwind CSS', 'JWT'],
      websiteUrl: 'https://heritedge-vy58.onrender.com/'
    },
    {
      id: 2,
      title: 'RAKSHA',
      description: 'RAKSHA is a secure web-based complaint management system that allows users to submit, track, and manage complaints with role-based access and real-time notifications.',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771057904/RAKSHA_ryalhp.png',
      technologies: ['React', 'TypeScript', 'Socket.io', 'Express', 'MongoDB'],
      githubUrl: 'https://github.com/Gayatrii4506/RAKSHA'
    },
    {
      id: 3,
      title: 'Arth संचय',
      description: 'AI-powered goal-based wealth planning platform that uses machine learning, portfolio optimization, and Monte Carlo simulation to generate personalized investment strategies.',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771057638/ArthSanchay_gqad1y.png',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'JWT'],
      githubUrl: 'https://github.com/Gayatrii4506/ArthSanchay',
      status: 'in-progress',
    },
    {
      id: 4,
      title: 'Student Management System',
      description: 'A comprehensive student portal with features for attendance tracking, grade management, course enrollment, and academic progress monitoring.',
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=400&fit=crop',
      technologies: ['HTML', 'CSS', 'JavaScript', 'PHP', 'MySQL'],
      githubUrl: 'https://github.com/Gayatrii4506/student-portal',
      status: 'in-progress',
    },
    {
      id: 5,
      title: 'Online Resume Builder',
      description: 'A Java-based application that allows users to create, customize, and download professional resumes quickly through a simple and user-friendly interface',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771062097/Screenshot_2026-02-14_151101_vqoiix.png',
      technologies: ['JavaFX', 'Swing', 'Spring Boot'],
      githubUrl: 'https://github.com/Gayatrii4506/recipe-finder',
      status: 'in-progress',
    },
    {
      id: 7,
      title: 'Shodh',
      description: 'Shodh is a modern full-stack web platform that empowers researchers to discover groundbreaking project ideas, form collaborative teams, and advance scientific innovation together',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771059904/Screenshot_2026-02-14_143349_usaaqh.png',
      technologies: ['React', 'Framer Motion', 'CSS3', 'Netlify'],
      githubUrl: 'https://github.com/Gayatrii4506/Shodh',
      status: 'in-progress',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <section id="projects" className="projects-section">
      <div className="section-container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Featured Projects
          </motion.h2>

          <div className="projects-grid">
            <AnimatePresence>
              {(showAll ? projects : projects.slice(0, INITIAL_COUNT)).map((project) => (
                <motion.div
                  key={project.id}
                  className={`project-card ${project.featured ? 'featured' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  layout
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="project-image-wrap">
                    {project.status === 'in-progress' && (
                      <div className="project-badge">In Progress</div>
                    )}
                    {project.status === 'client-project' && (
                      <div className="project-badge client-badge">Client Project</div>
                    )}
                    <img src={project.image} alt={project.title} loading="lazy" />
                    <div className="project-overlay">
                      <div className="project-overlay-buttons">
                        {project.status === 'in-progress' ? (
                          <button className="btn-overlay btn-disabled" disabled>
                            Coming Soon
                          </button>
                        ) : null}
                        
                        {project.githubUrl && (
                          <a
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-overlay btn-github"
                          >
                            <FiGithub />
                            View on GitHub
                          </a>
                        )}

                        {project.websiteUrl && (
                          <a
                            href={project.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-overlay btn-github"
                          >
                            <FiExternalLink />
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="project-info">
                    <h3 className="project-title">{project.title}</h3>
                    <p className="project-description">{project.description}</p>
                    <div className="project-tech-tags">
                      {project.technologies.map((tech, i) => (
                        <span key={i} className="project-tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {projects.length > INITIAL_COUNT && (
            <motion.div className="view-toggle-wrap" variants={itemVariants}>
              <motion.button
                className="btn btn-secondary view-toggle-btn"
                onClick={() => setShowAll(!showAll)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {showAll ? <FiChevronUp /> : <FiChevronDown />}
                {showAll ? 'View Less' : 'View More'}
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;